package com.solevo.dist.pages.orderdetails

import androidx.compose.runtime.*
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.navigation.NavigationConstants.ORDER_ID_PARAM
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import org.w3c.dom.url.URLSearchParams
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import org.jetbrains.compose.web.css.px
import com.solevo.dist.viewmodels.OrderDetailsViewModel
import com.solevo.dist.viewmodels.OrderDetailsUiState
import com.solevo.dist.viewmodels.ProductsTab
import com.solevo.dist.components.CenteredLoader
import com.solevo.dist.components.EmptyState
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgba
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import kotlinx.datetime.Instant
import com.solevo.dist.navigation.Screen
import com.solevo.dist.util.Constants
import com.solevo.dist.util.Res
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.application.models.InvoiceStatus
import com.solevo.dist.util.StatusMapper
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableBodyCellBold
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.secondaryText
import com.solevo.dist.theme.styles.sectionTitle
import kotlinx.serialization.encodeToString


@Page("/orderdetails/order")
@Composable
fun OrderDetailsScreen() {
    RequireAuth { OrderDetailsPage() }
}

@Composable
fun OrderDetailsPage() {
    val orderId = remember {
        val params = URLSearchParams(window.location.search)
        params.get(ORDER_ID_PARAM) ?: ""
    }

    WaitForAuthAndViewModel(
        key = orderId,
        viewModelFactory = { OrderDetailsViewModel(orderId) }
    ) { vm ->
        OrderDetailsContent(vm)
    }
}

@Composable
private fun OrderDetailsContent(vm: OrderDetailsViewModel) {
    val ui by vm.state.collectAsState()

    DashboardPageLayout(header = {
        DetailsHeader(ui) { vm.selectTab(it) }
    }) { DetailsBody(ui) }
}

@Composable
private fun DetailsHeader(ui: OrderDetailsUiState, onTabSelected: (ProductsTab) -> Unit) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        SpanText(
            "${strings.wordOrder} ${ui.header.id}",
            Modifier.fontSize(28.px).fontWeight(FontWeight.Bold)
        )
        Box(Modifier.height(16.px))

        // Info tiles as two flex rows with equal-width columns (keeps rows aligned)
        Row(Modifier.fillMaxWidth().gap(24.px)) {
            InfoTile(
                title = strings.labelCustomerReference,
                value = ui.header.referenceText,
                iconSrc = Res.Icon.OrderIcons.reference
            )
            InfoTile(
                title = strings.labelOrderId,
                value = ui.header.id,
                iconSrc = Res.Icon.OrderIcons.numbers
            )
            InfoTile(
                title = strings.labelStatus,
                value = ui.header.statusText,
                iconSrc = Res.Icon.OrderIcons.history
            )
        }
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth().gap(24.px)) {
            InfoTile(
                title = strings.labelRemainingAmount,
                value = ui.header.totalAmountText,
                iconSrc = Res.Icon.OrderIcons.accountBalance
            )
            InfoTile(
                title = strings.labelDateCreated,
                value = ui.header.createdText,
                iconSrc = Res.Icon.OrderIcons.calendar
            )
            InfoTile(
                title = strings.labelPickupDate,
                value = ui.header.pickupText,
                iconSrc = Res.Icon.OrderIcons.calendar
            )
        }

        Box(Modifier.height(16.px))

        // Tabs identical styling to Orders
        Box(
            Modifier
                .fillMaxWidth()
                .position(Position.Relative)
        ) {
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px)
                    .right(0.px)
                    .bottom(1.px)
                    .height(1.px)
                    .backgroundColor(rgb(230, 232, 235))
                    .zIndex(0)
            )

            Row(
                Modifier
                    .gap(24.px)
                    .align(Alignment.BottomStart)
                    .zIndex(1)
            ) {
                ProductsTab.values().forEach { t ->
                    TopTab(
                        label = when (t) {
                            ProductsTab.All -> strings.allItems
                            ProductsTab.Remaining -> strings.labelRemaining
                            ProductsTab.DeliveryNote -> strings.labelDeliveryNotes
                            ProductsTab.Invoices -> strings.labelInvoices
                        }, selected = ui.selectedTab == t
                    ) { onTabSelected(t) }
                }
            }
        }
    }
}

@Composable
private fun TopTab(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier { if (selected) property("box-shadow", "inset 0 -3px 0 0 black") }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier
                .fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}

@Composable
private fun InfoTile(
    title: String,
    value: String,
    iconSrc: String = "/images/ic_clinical_notes.png"
) {
    Row(
        Modifier
            .flex(1, 1, 0.px) // equal width columns
            .gap(12.px)
            .alignItems(AlignItems.Center)
    ) {
        // Icon image
        Image(
            modifier = Modifier
                .size(18.px)
                .borderRadius(4.px)
                .styleModifier { property("object-fit", "cover") },
            src = iconSrc,
            alt = "Info icon"
        )

        Column {
            SpanText(title, Modifier.secondaryText())
            Box(Modifier.height(4.px))
            SpanText(value.ifBlank { "—" }, Modifier.tableBodyCell())
        }
    }
}

@Composable
private fun DetailsBody(ui: OrderDetailsUiState) {
    Box(Modifier.padding(24.px).fillMaxWidth()) {
        when {
            ui.loading -> CenteredLoader()
            ui.error != null -> {
                val s = LocalStrings.current
                SpanText("${s.errorPrefix}: ${ui.error}")
            }

            else -> DetailsTable(ui)
        }
    }
}

@Composable
private fun DetailsTable(ui: OrderDetailsUiState) {
    val context = rememberPageContext()
    Column(Modifier.fillMaxWidth()) {
        when (ui.selectedTab) {
            ProductsTab.All, ProductsTab.Remaining -> {
                // header row for products
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier {
                            property(
                                "grid-template-columns",
                                "1.2fr 0.8fr 1fr 0.6fr 0.8fr"
                            )
                        }
                        .tableHeaderRow()
                ) {
                    val s = LocalStrings.current
                    SpanText(s.headerProduct, Modifier.tableHeaderCell())
                    SpanText(s.headerTotal, Modifier.tableHeaderCell())
                    SpanText(s.headerWarehouse, Modifier.tableHeaderCell())
                    SpanText(s.headerUnits, Modifier.tableHeaderCell())
                    SpanText(s.headerPricePerUnit, Modifier.tableHeaderCell())
                }

                val items = if (ui.selectedTab == ProductsTab.Remaining)
                    ui.remainingItems.filter { (it.remainingQuantity ?: 0.0) != 0.0 }
                else ui.remainingItems

                if (items.isEmpty()) {
                    if (ui.selectedTab == ProductsTab.Remaining) {
                        EmptyState(
                            title = "No Remaining Items",
                            message = "You have no remaining items in this order."
                        )
                    } else {
                        EmptyState(
                            title = "No Items",
                            message = "This order has no items to display."
                        )
                    }
                    return@Column
                }

                items.forEach { item ->
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .display(DisplayStyle.Grid)
                            .styleModifier {
                                property(
                                    "grid-template-columns",
                                    "1.2fr 0.8fr 1fr 0.6fr 0.8fr"
                                )
                            }
                            .tableRow()
                            .cursor(Cursor.Pointer)
                            .onClick {
                                context.router.navigateTo(Screen.ProductDetailsPage.withId(item.productId))
                            }
                    ) {
                        SpanText(item.name.ifBlank { "—" }, Modifier.tableBodyCell())
                        SpanText(item.totalPrice?.let { AmountFormatter.formatAmountWithCurrency(it) }
                            ?: "—", Modifier.tableBodyCell())
                        SpanText(item.warehouse ?: "-", Modifier.tableBodyCell())
                        SpanText(
                            AmountFormatter.formatUnits(item.quantity, item.unit),
                            Modifier.tableBodyCell()
                        )
                        SpanText(item.pricePerUnit?.let {
                            AmountFormatter.formatPricePerUnit(
                                it,
                                item.unit
                            )
                        }
                            ?: "—", Modifier.tableBodyCell())
                    }
                }
            }

            ProductsTab.DeliveryNote -> {
                // header for delivery notes with fixed two-column grid
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier {
                            property("grid-template-columns", "300px max-content")
                            property("column-gap", "24px")
                        }
                        .tableHeaderRow()
                ) {
                    val s = LocalStrings.current
                    SpanText(s.headerNote, Modifier.tableHeaderCell())
                    SpanText(s.headerDate, Modifier.tableHeaderCell())
                }

                if (ui.deliveryNotes.isEmpty()) {
                    val s = LocalStrings.current
                    EmptyState(title = s.noDeliveryNotesTitle, message = s.noDeliveryNotesMsg)
                    return@Column
                }

                ui.deliveryNotes.forEachIndexed { index, dn ->
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .display(DisplayStyle.Grid)
                            .styleModifier {
                                property("grid-template-columns", "300px max-content")
                                property("column-gap", "24px")
                            }
                            .tableRow()
                            .cursor(Cursor.Pointer)
                            .onClick {
                                val json = kotlinx.serialization.json.Json.encodeToString(
                                    com.solevo.dist.application.models.DeliveryNote.serializer(),
                                    dn
                                )
                                kotlinx.browser.window.localStorage.setItem("delivery_note", json)
                                context.router.navigateTo(com.solevo.dist.navigation.Screen.DeliveryNotePage.route)
                            }
                    ) {
                        val s = LocalStrings.current
                        SpanText("${s.labelDeliveryNote} ${index + 1}", Modifier.tableBodyCell())
                        SpanText(
                            DateFormatter.formatDateLong(dn.pickingDate),
                            Modifier.tableBodyCell()
                        )
                    }
                }
            }

            ProductsTab.Invoices -> {
                // header for invoices with four columns
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier {
                            property(
                                "grid-template-columns",
                                "1.2fr 0.6fr 0.8fr 0.8fr"
                            )
                        }
                        .tableHeaderRow()
                ) {
                    val s = LocalStrings.current
                    SpanText(s.headerInvoice, Modifier.tableHeaderCell())
                    SpanText(s.labelStatus.uppercase(), Modifier.tableHeaderCell())
                    SpanText(s.headerDueDate, Modifier.tableHeaderCell())
                    SpanText(s.headerTotal, Modifier.tableHeaderCell())
                }

                if (ui.invoices.isEmpty()) {
                    val s2 = LocalStrings.current
                    EmptyState(title = s2.noInvoicesTitle, message = s2.noInvoicesMsg)
                    return@Column
                }

                ui.invoices.forEach { tx ->
                    val statusInfo = StatusMapper.mapInvoiceStatus(tx.invoiceStatus)
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .display(DisplayStyle.Grid)
                            .styleModifier {
                                property(
                                    "grid-template-columns",
                                    "1.2fr 0.6fr 0.8fr 0.8fr"
                                )
                            }
                            .tableRow()
                            .cursor(Cursor.Pointer)
                            .onClick {
                                val payload = InvoicePayload(
                                    transactionId = tx.transactionId,
                                    invoiceId = tx.invoiceId ?: tx.invoiceNumber.orEmpty(),
                                    transactionType = Transaction.Type.Invoice.toString()
                                )
                                val json = kotlinx.serialization.json.Json.encodeToString(payload)
                                window.localStorage.setItem("invoice_details", json)
                                context.router.navigateTo(Screen.InvoiceDetailsPage.route)
                            }
                    ) {
                        SpanText(tx.invoiceNumber ?: tx.transactionId, Modifier.tableBodyCell())
                        Row(Modifier.alignItems(AlignItems.Center).gap(8.px)) {
                            statusInfo.iconSrc?.let { Image(src = it, alt = "status") }
                            val statusColor = when (statusInfo.colorHex) {
                                "#DD3636" -> rgb(221, 54, 54)
                                "#B17700" -> rgb(177, 119, 0)
                                "#01A36C" -> rgb(1, 163, 108)
                                else -> rgb(36, 41, 47)
                            }
                            SpanText(
                                statusInfo.label,
                                Modifier.tableBodyCell().color(statusColor)
                            )
                        }
                        SpanText(
                            DateFormatter.formatDateShort(tx.invoiceDueDate),
                            Modifier.tableBodyCell()
                        )
                        SpanText(
                            AmountFormatter.formatAmountWithCurrency(invoiceTotalForDisplay(tx)),
                            Modifier.tableBodyCell()
                        )
                    }
                }
            }
        }
    }
}





// mapped via InvoiceStatusUI companion; helper removed

private fun invoiceTotalForDisplay(tx: Transaction): Double {
    val base =
        if (tx.originalAmount != 0.0) kotlin.math.abs(tx.originalAmount) else kotlin.math.abs(tx.amount)
    return -base
}



